import { useEffect, useState } from 'react';
import type { Role } from '@bingo/shared';
import { ApiError, api } from '../lib/api.js';

/**
 * Quem opera este evento.
 *
 * Antes da designação explícita, o operador só enxergava eventos em que já
 * tinha agido — e um operador recém-convidado não via nada, justamente no
 * dia em que mais precisava. Aqui quem administra marca quem opera o quê.
 *
 * OWNER e ADMIN continuam vendo tudo: a lista existe para dar acesso, não
 * para tirar.
 */

interface Candidate {
  membershipId: string;
  name: string;
  email: string;
  role: Role;
}

export function EventOperators({ eventId }: { eventId: string }) {
  const [candidates, setCandidates] = useState<Candidate[] | null>(null);
  const [assigned, setAssigned] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    void api<{ candidates: Candidate[]; assigned: string[] }>(`/api/events/${eventId}/operators`)
      .then((data) => {
        setCandidates(data.candidates);
        setAssigned(data.assigned);
      })
      .catch(() => setCandidates([]));
  }, [eventId]);

  const toggle = async (membershipId: string, checked: boolean) => {
    const next = checked
      ? [...assigned, membershipId]
      : assigned.filter((id) => id !== membershipId);

    setAssigned(next);
    setBusy(true);
    setError(null);
    setSaved(false);

    try {
      // Salva na hora: uma tela de marcar caixinhas com botão "Salvar" é
      // onde a pessoa marca, sai e descobre depois que não valeu.
      await api(`/api/events/${eventId}/operators`, {
        method: 'PUT',
        body: { membershipIds: next },
      });
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setAssigned(assigned);
      setError(err instanceof ApiError ? err.message : 'Não foi possível salvar.');
    } finally {
      setBusy(false);
    }
  };

  if (candidates === null) return <p className="hint-text">Carregando…</p>;

  const operadores = candidates.filter((c) => c.role === 'OPERATOR');

  return (
    <div className="operators">
      {operadores.length === 0 ? (
        <p className="hint-text">
          Nenhum operador na organização ainda. Convide alguém em <strong>Membros</strong>.
        </p>
      ) : (
        <div className="checks">
          {operadores.map((candidate) => (
            <label key={candidate.membershipId} className="check">
              <input
                type="checkbox"
                checked={assigned.includes(candidate.membershipId)}
                disabled={busy}
                onChange={(event) => void toggle(candidate.membershipId, event.target.checked)}
              />
              <span>
                {candidate.name}
                <small>{candidate.email}</small>
              </span>
            </label>
          ))}
        </div>
      )}

      {error ? (
        <p className="error" role="alert">
          {error}
        </p>
      ) : null}
      {saved ? <p className="hint-text">Salvo.</p> : null}

      <p className="hint-text">Donos e administradores veem todos os eventos.</p>
    </div>
  );
}
